"use client";

import { usePathname } from "next/navigation";
import {
  createContext,
  type ReactNode,
  useContext,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import { type BreadcrumbItem, pageBreadcrumbItems } from "./breadcrumb-routes";

export type { BreadcrumbItem } from "./breadcrumb-routes";

const HeaderTarget = createContext<{
  target: HTMLDivElement | null;
  setTarget: (target: HTMLDivElement | null) => void;
}>({ target: null, setTarget: () => {} });

const desktopQuery = "(min-width: 768px)";
function subscribe(onChange: () => void) {
  const media = window.matchMedia(desktopQuery);
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}
function isDesktop() {
  return window.matchMedia(desktopQuery).matches;
}
function serverSnapshot() {
  return false;
}

export function BreadcrumbsProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<HTMLDivElement | null>(null);
  return (
    <HeaderTarget.Provider value={{ target, setTarget }}>
      {children}
    </HeaderTarget.Provider>
  );
}

export function BreadcrumbsSlot() {
  const { setTarget } = useContext(HeaderTarget);
  return <div className="header-breadcrumb" ref={setTarget} />;
}

export function PageBreadcrumbs({
  items,
}: {
  items?: readonly BreadcrumbItem[];
}) {
  const pathname = usePathname();
  const { target } = useContext(HeaderTarget);
  const desktop = useSyncExternalStore(subscribe, isDesktop, serverSnapshot);
  const resolved = items ?? pageBreadcrumbItems(pathname);
  if (resolved.length < 2) return null;
  const navigation = (
    <nav className="page-breadcrumbs" aria-label="パンくずリスト">
      <ol>
        {resolved.map((item, index) => (
          <li key={item.href ?? item.label}>
            {index > 0 && <span aria-hidden="true">/</span>}
            {index === resolved.length - 1 ? (
              <span aria-current="page">{item.label}</span>
            ) : item.onClick ? (
              <button type="button" onClick={item.onClick}>
                {item.label}
              </button>
            ) : item.href ? (
              <a href={item.href}>{item.label}</a>
            ) : (
              <span>{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
  return desktop && target ? createPortal(navigation, target) : navigation;
}
